import { z } from 'zod';
export const RecurringInvoicePeriodicitySchema = z.enum([
    'MONTHLY',
    'BIMONTHLY',
    'QUARTERLY',
    'SEMIANNUAL',
    'YEARLY',
]);
export const RecurringInvoicePeriodicityLabel = {
    MONTHLY: 'Mensual',
    BIMONTHLY: 'Bimestral',
    QUARTERLY: 'Trimestral',
    SEMIANNUAL: 'Semestral',
    YEARLY: 'Anual',
};
export const RecurringInvoiceStatusSchema = z.enum(['ACTIVE', 'PAUSED', 'CANCELED', 'COMPLETED']);
export const RecurringInvoiceStatusLabel = {
    ACTIVE: 'Activa',
    PAUSED: 'Pausada',
    CANCELED: 'Cancelada',
    COMPLETED: 'Completada',
};
/**
 * Ancla de inicio del mantenimiento recurrente: 'DATE' usa una fecha concreta;
 * las fases de ejecución hacen que la iguala empiece a facturarse al llegar el
 * proyecto a esa fase (creada PAUSED y activada al alcanzarla). Vive aquí (y no
 * en opportunities.ts) para que el DTO de iguala pueda usarlo sin ciclo.
 */
export const RecurringStartAnchorSchema = z.enum([
    'DATE',
    'ONBOARDING',
    'EJECUCION',
    'FINALIZADO',
    'SOPORTE',
]);
const decimalString = z
    .union([z.number(), z.string()])
    .transform((v) => (typeof v === 'number' ? v.toString() : v));
export const RecurringInvoiceLineInputSchema = z.object({
    position: z.number().int().min(0).default(0),
    /** F35 — opcional: producto del catálogo del que se prellenan description/unitPriceCents/vatRate si no se especifican. */
    productId: z.string().nullable().optional(),
    /** Requerido salvo que llegue productId. */
    description: z.string().min(1).max(500).optional(),
    quantity: decimalString.default('1'),
    /** Requerido salvo que llegue productId. */
    unitPriceCents: z.number().int().optional(),
    /** Coste interno por unidad (mismo criterio que unitPriceCents). Campo interno (margen). */
    costCents: z.number().int().min(0).default(0),
    discountPct: decimalString.default('0'),
    /** Requerido salvo que llegue productId. */
    vatRate: decimalString.optional(),
    isSupplied: z.boolean().default(false),
});
export const RecurringInvoiceLineDtoSchema = z.object({
    id: z.string(),
    productId: z.string().nullable(),
    position: z.number().int(),
    description: z.string(),
    quantity: decimalString,
    unitPriceCents: z.number().int(),
    costCents: z.number().int(),
    discountPct: decimalString,
    vatRate: decimalString,
    isSupplied: z.boolean(),
});
export const RecurringInvoiceDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientDisplayName: z.string(),
    opportunityId: z.string().nullable(),
    opportunityCode: z.string().nullable(),
    seriesId: z.string().nullable(),
    seriesPrefix: z.string().nullable(),
    name: z.string(),
    notes: z.string().nullable(),
    periodicity: RecurringInvoicePeriodicitySchema,
    dayOfMonth: z.number().int(),
    irpfRate: z.string(),
    status: RecurringInvoiceStatusSchema,
    nextRunDate: z.string().datetime(),
    /**
     * Fecha de la PRIMERA cuota: la anotada al generarla (`firstRunDate`) o, si el plan aún no ha generado ninguna, su próxima
     * emisión (la primera prevista; con `generatedCount` 0 esta fecha todavía puede moverse).
     */
    firstRunDate: z.string().datetime(),
    lastRunAt: z.string().datetime().nullable(),
    generatedCount: z.number().int(),
    /** F42/M6 — límite del contrato: nº máximo de cuotas y/o fecha fin. null+null = sin fecha límite (alerta de renovación). */
    maxOccurrences: z.number().int().nullable(),
    endsAt: z.string().datetime().nullable(),
    /** Ancla de arranque por fase (mantenimiento diferido): iguala PAUSED hasta llegar a ella. null = sin ancla. */
    startAnchor: RecurringStartAnchorSchema.nullable(),
    createdAt: z.string().datetime(),
    createdByName: z.string().nullable(),
    lines: z.array(RecurringInvoiceLineDtoSchema),
});
export const CreateRecurringInvoiceSchema = z.object({
    clientId: z.string(),
    opportunityId: z.string().nullable().optional(),
    seriesId: z.string().nullable().optional(),
    name: z.string().min(2).max(200),
    notes: z.string().max(2000).nullable().optional(),
    periodicity: RecurringInvoicePeriodicitySchema.default('MONTHLY'),
    dayOfMonth: z.coerce.number().int().min(1).max(28).default(1),
    irpfRate: decimalString.optional(),
    /** Fecha de la primera factura a generar (yyyy-mm-dd o ISO). Si null, calculamos a partir de periodicity+dayOfMonth desde hoy. */
    startDate: z.string().nullable().optional(),
    /** M6 — límite del contrato (opcional): nº de cuotas y/o fecha fin. */
    maxOccurrences: z.number().int().positive().nullable().optional(),
    endsAt: z.string().nullable().optional(),
    lines: z.array(RecurringInvoiceLineInputSchema).min(1),
});
export const UpdateRecurringInvoiceSchema = CreateRecurringInvoiceSchema.partial().extend({
    status: RecurringInvoiceStatusSchema.optional(),
    nextRunDate: z.string().nullable().optional(),
});
export const RecurringInvoiceListResponseSchema = z.object({
    items: z.array(RecurringInvoiceDtoSchema),
});
// ---------------------------------------------------------------------------
// ARR / MRR de los pagos recurrentes (mini dashboard de «Pagos recurrentes»)
// ---------------------------------------------------------------------------
/** Filtros del dashboard: año y, opcionalmente, mes (1-12). Sin mes = el año entero. */
export const RecurringMetricsQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
});
/** Una fotografía de los pagos recurrentes en un mes. Importes en céntimos, base imponible (sin IVA ni suplidos). */
export const RecurringMetricsPointSchema = z.object({
    /** «YYYY-MM» */
    month: z.string().regex(/^\d{4}-\d{2}$/),
    /** Ingreso mensual recurrente: la suma de los planes vigentes, cada uno llevado a un mes. */
    mrrCents: z.number().int(),
    /** Ingreso anual recurrente: lo que sumarían en doce meses los planes vigentes ese mes. */
    arrCents: z.number().int(),
    plans: z.number().int(),
    clients: z.number().int(),
    /** MRR de los planes que EMPIEZAN este mes. */
    newMrrCents: z.number().int(),
    /** MRR de los planes que DEJAN de contar a partir de este mes (terminan, se cancelan). */
    lostMrrCents: z.number().int(),
    /** true = el mes aún no ha llegado: es lo que se facturaría si nada cambia. */
    projected: z.boolean(),
});
/** Una porción de la composición del MRR de un mes: por cliente o por periodicidad. */
export const RecurringMetricsSliceSchema = z.object({
    /** El cliente (clientId) o la periodicidad (MONTHLY…). */
    key: z.string(),
    /** Nombre a mostrar: el del cliente, o la propia periodicidad. */
    label: z.string(),
    arrCents: z.number().int(),
    mrrCents: z.number().int(),
    plans: z.number().int(),
    /** Parte del ARR total en puntos básicos (10.000 = 100 %). */
    shareBps: z.number().int(),
});
export const RecurringMetricsResponseSchema = z.object({
    year: z.number().int(),
    /** El mes elegido; null = «todo el año». */
    month: z.number().int().nullable(),
    /** Mes al que se refieren las cifras grandes: el elegido o, sin mes, el actual (año en curso) o diciembre. */
    reference: RecurringMetricsPointSchema,
    /** El mes anterior y el mismo mes del año anterior, para ver la variación. */
    previousMonth: RecurringMetricsPointSchema,
    previousYear: RecurringMetricsPointSchema,
    /** Los doce meses del año elegido. */
    months: z.array(RecurringMetricsPointSchema).length(12),
    /** De qué se compone el MRR del mes de referencia: por cliente y por periodicidad, de mayor a menor. */
    composition: z.object({
        byClient: z.array(RecurringMetricsSliceSchema),
        byPeriodicity: z.array(RecurringMetricsSliceSchema),
    }),
    /** Años seleccionables (desde el primer plan hasta el año que viene). */
    years: z.array(z.number().int()),
});
//# sourceMappingURL=recurring.js.map